import {
  type HTMLAttributes,
  type MouseEvent,
  type MouseEventHandler,
  type RefCallback,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import {type DraggableSyntheticListeners, useDraggable} from '../drag/dnd-kit'
import {BOARD_CARD_DRAG_TYPE, type BoardCardDragData} from '../drag/types'
import {cellPosition} from '../focus/positions'
import type {BoardFocusPosition} from '../focus/types'
import {useRovingFocus} from '../focus/use-roving-focus'
import {composeCellKey} from '../provider/cell-key'
import {useBoardCardDragPolicy} from '../provider/contexts'
import {useBoardDialogReport} from '../reporting/contexts'
import type {BoardSelectedCard, BoardSelectionIntent} from '../selection/types'
import {useBoardCardSelection} from '../selection/use-board-card-selection'
import type {CellAddress, ItemId} from '../types'
import {useElementRef} from '../use-element-ref'
import {isSomebodyElsesEvent} from './own-card-event'

export type UseBoardCardResult = {
  cardProps: HTMLAttributes<HTMLElement> & {ref: RefCallback<HTMLElement>}
  /**
   * Whether the caller states this card as selected.
   *
   * Nothing on the board acts on it and nothing draws it. It is here so a caller can,
   * and so a styled layer can put a treatment on a card without reaching for the
   * selection itself.
   */
  isSelected: boolean
  /**
   * Whether a pointer has hold of this card right now.
   *
   * **Wider than dnd-kit's own `isDragging`, which this shadows the name of.** That one
   * turns true once the pointer has moved past the sensor's activation distance, which is
   * what keeps click-to-select reliable and what makes it the wrong thing to draw from: a
   * reader has picked a card up at the press, and a treatment that waits for movement
   * arrives after the gesture it is describing. So this is true from a press the drag
   * library is going to act on -- one on a control the caller drew inside the card is that
   * control's, a shift-press is the range gesture's, a right-click is the platform's, and
   * a card the caller gave no id cannot be picked up at all -- and stays true for as long
   * as the drag that press becomes.
   *
   * It is the card rather than the item: the same item drawn in another cell is another
   * card and answers `false`, which is what makes this safe to put a treatment on.
   *
   * The board draws nothing for it. A styled layer marks the held card itself --
   * `@github-ui/board` puts `data-dragging` on it and rings it -- and the card under the
   * pointer is a copy drawn by {@link BoardDragOverlay} rather than this card moving.
   */
  isDragging: boolean
  /**
   * Why the caller refuses to let this card be dragged, or absent because it does not.
   *
   * The caller's own words, handed back unchanged. The board neither shortens them nor
   * substitutes any of its own, because it holds no rule about who may move what: this is
   * the string that came back from `BoardProvider`'s `getItemDragState`, and nothing else.
   *
   * Present exactly when a refusal is. It is **not** the answer to "can this card be
   * dragged": a card whose `getItemId` returned an empty string cannot be dragged either,
   * and it has no refusal, because the caller stated none and this package has no wording
   * to invent one with. Something drawing an affordance from this is drawing the refusal
   * it can explain, which is the only one worth drawing.
   *
   * `@github-ui/board` puts `data-drag-disabled` on a card that has one, and stops it
   * claiming a `cursor: grab` it cannot honour.
   */
  dragRefusal?: string
  /**
   * Makes one selection gesture on this card, as though it had been clicked that way.
   *
   * `cardProps` already carries the pointer gesture, so this is for a caller that offers
   * another way in: a checkbox on a card, a bulk-select control, a key of its own. It
   * changes nothing here either -- the next selection goes to the caller's
   * `onSelectionChange`, and comes back as `selectedCards` or does not.
   */
  selectCard: (intent: BoardSelectionIntent) => void
  /**
   * Says this card has opened or closed a dialog, a menu or anything else that means the
   * reader is acting on it.
   *
   * The one thing about a card the board cannot see for itself. A caller draws whatever it
   * likes inside a card, and a menu usually renders through a portal, so nothing the board
   * could watch would tell it. Call it with `true` when yours opens and `false` when it
   * closes; a card taken off the board with one open is reported closed on the way out, so
   * an unmount is not something to handle.
   *
   * It reaches the caller's `onCardDialogChange` and changes nothing here. The board holds
   * no dialog state and draws nothing for one.
   */
  reportDialogOpen: (isOpen: boolean) => void
}

/**
 * One card, in one cell.
 *
 * A card is the reason behaviour is exposed as hooks rather than as a `getCardProps`
 * function: making a card draggable calls a hook, and a hook cannot be called inside a
 * callback that runs once per card. So the caller renders a component per card and that
 * component calls this. Selection arrived that way and no caller changed, and so did the
 * drag source; everything a card gains later arrives the same way.
 *
 * It hands back the card's stable element id, and the card's share of the board's roving
 * tabindex. Two things make the id safe to point `aria-activedescendant` and
 * `aria-labelledby` at: `useId` is per component instance, so no two cards share a prefix
 * even when the same item appears in two cells; and the cell-and-item suffix survives
 * escaping without collapsing, so it names the card rather than merely decorating it.
 *
 * Exactly one element on the whole board carries `tabIndex: 0`, and it is whichever card
 * or cell the focus position names. A card is a position in the data, so the card that is
 * the board's tab stop can be one the board's window has scrolled past; when it renders
 * again it takes focus, without the position ever having pointed at an element.
 *
 * `cardProps` also carries the pointer gesture that selects: a click on the card selects
 * it alone, `Ctrl` or `Cmd` puts it in or out of what is already selected, and `Shift`
 * runs a range from the last card either of those named, **inside that card's cell and
 * nowhere else**. Empty the selection, whether by taking the last card out or by clearing
 * it yourself, and there is nowhere left to run a range from, so the next `Shift` starts
 * one where it was made. `Space` and `Shift+Space` are the same two gestures from the
 * keyboard. A click or a key that landed on a control the caller drew inside the card
 * belongs to that control and is left alone. Nothing here holds a selection: the gesture
 * is reported to the caller, which states what is selected.
 *
 * `cardProps` also makes the card a drag source, for pointer input only. A press and a
 * short movement picks the card up and the board reports it through the caller's
 * `onDragChange`; a shift-click runs a range instead, because the range gesture takes the
 * event's default and a prevented default is one dnd-kit will not start a drag on. A press
 * on a control the caller drew inside the card, or anywhere in a menu the card opened, is
 * that control's and drags nothing, which is the rule a click on one already follows. The
 * card being held says so through `isDragging`, and it stays where it is: a copy of it
 * follows the pointer instead, drawn by whoever renders {@link BoardDragOverlay}. The drop
 * that acts on the move is https://github.com/github/views/issues/99.
 *
 * **Two cards cannot be picked up, for two reasons that are not the same reason.** One is
 * a card the caller's own `getItemDragState` refused, which hands back its reason through
 * `dragRefusal` and is the refusal anything drawing an affordance should draw. The other
 * is a card whose `getItemId` returned an empty string, which has no identity to name in a
 * move; the caller stated no reason for that and this package has none of its own, so it
 * says nothing about it. Neither card is otherwise inert: both still take focus, both take
 * the board's tab stop when the focus position names them, and both still select.
 */
export function useBoardCard(itemId: ItemId, cell: CellAddress): UseBoardCardResult {
  const baseId = useId()
  const {ref, setRef} = useElementRef()
  const {columnKey, rowKey} = cell

  // Keyed on the two halves rather than on `cell`, because callers build the address
  // inline and a fresh object every render would rebuild every subscription this card
  // holds on every render. This one is the card's identity: what a selection holds, what a
  // dialog is reported against, and what a drag names.
  const card = useMemo<BoardSelectedCard>(() => ({columnKey, rowKey, itemId}), [columnKey, rowKey, itemId])
  // The same three keys, and a separate object because a position also says what kind of
  // thing it is at. Kept apart rather than widened, so a card stays a plain card and
  // nothing has to strip a field off one before storing it.
  //
  // A card takes focus reported from anywhere inside it, because a caller draws what it
  // likes in one and a link in a card is a tab stop in its own right. Without that, focus
  // could sit in one card while the board's tab stop, and the next arrow key, were still
  // on another.
  const position = useMemo<BoardFocusPosition>(
    () => cellPosition({columnKey, rowKey}, itemId),
    [columnKey, rowKey, itemId],
  )
  const {isRovingItem, onFocus} = useRovingFocus(position, ref, {fromDescendants: true})
  const {isSelected, selectCard, onClick, onMouseDown, onKeyDown} = useBoardCardSelection(card)

  // The cell and the item together, because the same item sits in every column it carries
  // a value for on a multi-value board, and in one cell per swimlane. A bare item id would
  // register one drag node from several cells and name one element in several places.
  const cellKey = composeCellKey({columnKey, rowKey})
  const cardKey = `${cellKey}:${itemId}`
  const dragData = useMemo<BoardCardDragData>(() => ({type: BOARD_CARD_DRAG_TYPE, ...card}), [card])

  /*
    The caller's answer for this card, and the whole of what decides whether it may be
    picked up. Nothing here reads a permission, asks what kind of thing the item is, or
    puts a rule of its own on top of the answer.

    Read through the provider's policy rather than off the config, so this hook -- the one
    that runs per drawn card -- wakes for the three things that can change the answer and
    not for every label and accessor sitting beside them. Resolving the caller's item from
    this address is the policy's too, because a card knows where it is while the caller's
    predicate takes the caller's own item.

    Absent for a card the caller allowed, for one its data no longer holds, and for one
    with no id to look it up by. Only the first of those is a card anybody stated anything
    about, which is why the other two carry no reason.

    Memoised because a card re-renders for things that say nothing about whether it may be
    dragged -- a press, a selection, focus arriving -- and the caller's own predicate runs
    in here. The policy's identity already stands for the data and both accessors behind
    it, so this recomputes exactly when the answer could have changed.
  */
  const dragRefusalFor = useBoardCardDragPolicy()
  const dragRefusal = useMemo(() => dragRefusalFor(cellKey, itemId), [dragRefusalFor, cellKey, itemId])

  /*
    The two ways a card ends up undraggable, kept apart because only one of them has
    anything to say. A card the caller refused carries the caller's reason; a card whose
    `getItemId` returned an empty string has nothing to name in a move and no reason
    anybody stated, so `dragRefusal` stays out of it.
  */
  const canBeDragged = itemId !== '' && dragRefusal === undefined

  const {
    setNodeRef,
    listeners,
    isDragging: hasSensorStarted,
  } = useDraggable({
    id: cardKey,
    data: dragData,
    // dnd-kit registers the node before it reads this and `disabled` withholds the
    // listeners, so a card that cannot be dragged is a registered node nothing can pick up
    // rather than a card with no node. That is also why two cards with no id in one cell
    // share an identifier and alias one another -- harmless only because neither can start
    // a drag.
    disabled: !canBeDragged,
  })

  /*
    Whether a pointer is down on this card, which is a few pixels of movement before
    dnd-kit will call the same gesture a drag.

    Held here rather than read off the sensor because the two answer different questions.
    The sensor's threshold exists so that a click stays a click, and it is the right rule
    for starting a drag and the wrong one for drawing a card: a reader has picked the card
    up at the press, and a ring that waits for movement arrives after the moment it is
    describing. Touch is the other way round -- a touch drag begins on a timer and never
    reaches the handler below -- so the card is drawn from whichever of the two says it is
    in hand.

    Set only for a press dnd-kit is going to take, which {@link isPressDndKitWillTake}
    settles: reading the press early is worth nothing if it reads presses the sensor
    refuses.
  */
  const [isPressed, setIsPressed] = useState(false)
  const isDragging = isPressed || hasSensorStarted

  // Merged here rather than by the caller: `board-core` cannot reach Primer's
  // `useMergedRefs`, and a card's element is one element however many things want to hold
  // it. Both inputs are stable, so a card's dnd-kit subscription is built once.
  const setCardRef = useMemo(() => mergeRefs(setRef, setNodeRef), [setRef, setNodeRef])

  /*
    Every one of dnd-kit's activators, gated on the press being the card's own.

    Neither sensor looks at what was pressed: they take any `mousedown` that is not a
    right-click and any single-finger `touchstart`, wherever it came from. So a drag from a
    card's title link, or from a slider a caller drew in one, would pick the whole card up
    while the control was being used. A menu the card opened is the case that cannot be
    seen in the markup at all: it renders through a portal, so the element is nowhere
    inside the card, and React routes the press to the card that rendered it anyway.

    The same question the selection gesture asks, and it has to be the same answer: a press
    that is not the card's is not the card's for either of them.

    Wrapped rather than named one by one, so a sensor mounted in
    `drag/BoardDragProvider.tsx` is gated by arriving rather than by somebody remembering.
  */
  const dragActivators = useMemo(() => ownEventsOnly(listeners), [listeners])

  /*
    The one activator that collides with a handler of the card's own, taken out of the
    spread so it can be composed with that handler rather than dropped by it. `undefined`
    on a card that cannot be dragged.
  */
  const startDrag = dragActivators?.onMouseDown

  /*
    The card's own handler first, dnd-kit's second, and the order is the behaviour.

    Both want `mousedown`. The card's takes the event's default on a shift-click, so
    selecting a range does not also smear a text highlight across every card between its
    two ends, and dnd-kit refuses to activate on an event whose default was already
    prevented. So this order is what leaves a shift-click a range rather than a drag.
    Reversed, the card would be picked up and the highlight drawn.
  */
  const onCardMouseDown = useCallback<MouseEventHandler<HTMLElement>>(
    event => {
      onMouseDown(event)
      /*
        Held only for a press that is going to become a drag. `startDrag` is `undefined` on
        a card that cannot be picked up at all -- one the caller refused as much as one
        with no identity to move -- and {@link isPressDndKitWillTake} asks the rest. A card
        drawn as held for a press the sensor is about to refuse says a gesture has begun
        that never will.

        After the handler above rather than before it, because the prevented default that
        question turns on is that handler's.
      */
      if (startDrag !== undefined && isPressDndKitWillTake(event)) setIsPressed(true)
      startDrag?.(event)
    },
    [onMouseDown, startDrag],
  )

  /*
    Let go when the pointer goes up, wherever it has got to by then. The window rather than
    the card, because a card being dragged is the one case where the pointer is certain not
    to be over it any more.

    Bound only while a card is held, so a board nobody is touching adds nothing to the
    window.
  */
  useEffect(() => {
    if (!isPressed) return

    const release = () => setIsPressed(false)

    window.addEventListener('mouseup', release)
    window.addEventListener('touchend', release, {passive: true})
    window.addEventListener('touchcancel', release)

    return () => {
      window.removeEventListener('mouseup', release)
      window.removeEventListener('touchend', release)
      window.removeEventListener('touchcancel', release)
    }
  }, [isPressed])

  /*
    The pointer going up is not the only way a card stops being held. A drag abandoned with
    `Escape` leaves the button down over a card that is back where it started, so dnd-kit
    letting go of a card it was carrying lets go of it here too. Watched rather than
    handled, so there is no second reading of a key dnd-kit already owns.
  */
  useEffect(() => {
    if (!hasSensorStarted) return

    return () => setIsPressed(false)
  }, [hasSensorStarted])

  const {reportDialogOpen: report} = useBoardDialogReport()
  // What this card last said, so the board can close a dialog for a card that is taken off
  // the board with one open. Without it, a cell paging a card out would leave the caller
  // deferring every change to a card nobody can see any more.
  const isDialogOpenRef = useRef(false)

  const reportDialogOpen = useCallback(
    (isOpen: boolean) => {
      isDialogOpenRef.current = isOpen
      report(card, isOpen)
    },
    [report, card],
  )

  useEffect(
    () => () => {
      if (isDialogOpenRef.current) report(card, false)
    },
    [report, card],
  )

  return {
    cardProps: {
      /*
        The activators of whichever sensors the drag provider mounted, spread rather than
        named so a sensor added there arrives here without this file changing. It goes
        first so the card's own handlers below win every collision, which means an
        activator sharing a name with one of them is dropped rather than composed:
        `onMouseDown` is the one that collides today and it is composed by hand below, and
        `onKeyDown` appearing here at all would mean a keyboard sensor had been mounted,
        which `drag/BoardDragProvider.tsx` is the place to settle rather than this one.

        dnd-kit's `attributes` are deliberately not spread. They would make the element a
        `button` with a `tabIndex` of 0, which would replace the card's `listitem` role and
        give the board one tab stop per drawn card instead of the single one the focus
        position names. Their `aria-roledescription` and `aria-describedby` announce a
        keyboard drag, which the pointer-only sensors mean there is none of;
        https://github.com/github/views/issues/48 brings both, together with the sensor.

        dnd-kit's `transform` is not passed through either, and that is a decision rather
        than an omission: a card sits in a cell that scrolls and windows what it holds, so
        translating one moves it inside a box that clips, and it slides under its own
        cell's edge instead of travelling across the board. The card stays put and a copy
        of it follows the pointer, which is what {@link BoardDragOverlay} draws.
      */
      ...dragActivators,
      id: `${baseId}${toElementId(cardKey)}`,
      ref: setCardRef,
      // `-1` at rest rather than absent, so clicking a card is how a reader moves the
      // board's position onto it.
      tabIndex: isRovingItem ? 0 : -1,
      onFocus,
      onClick,
      onMouseDown: onCardMouseDown,
      onKeyDown,
    },
    isSelected,
    isDragging,
    dragRefusal,
    selectCard,
    reportDialogOpen,
  }
}

/**
 * The card's element, handed to both of the things that hold it.
 *
 * Primer's `useMergedRefs` does this, and a packaging test stops this package reaching for
 * it. Narrower than that one on purpose: a ref here returns nothing, so there is no
 * cleanup function to carry back, and React calls this with `null` on the way out.
 */
function mergeRefs(...refs: ReadonlyArray<RefCallback<HTMLElement>>): RefCallback<HTMLElement> {
  return element => {
    for (const ref of refs) ref(element)
  }
}

type CardActivators = Record<string, (event: SyntheticEvent<HTMLElement>) => void>

/**
 * The one button `MouseSensor` refuses. dnd-kit's own `MouseButton.RightClick`, which it
 * keeps to itself rather than exporting.
 */
const RIGHT_CLICK = 2

/**
 * Whether this press is one dnd-kit is going to pick the card up on.
 *
 * Asked because a card is drawn as held from the press itself rather than from the
 * sensor's own `isDragging`, which is the whole reason a ring answers the grab rather than
 * trailing it. dnd-kit settles the same question a moment later and settles it silently,
 * so a press it is about to refuse would leave a card ringed for a drag that never starts.
 *
 * Three refusals, and the card has to make all three to draw what the sensor will do:
 *
 * - A press on a control the caller drew inside the card is that control's. The rule the
 *   selection gesture applies and the one `ownEventsOnly` puts on every activator.
 * - dnd-kit declines a press whose default was already prevented, which is what leaves a
 *   shift-click a range rather than a drag. Asked of the event rather than of `Shift`, so
 *   any other handler that takes a press is answered without this file naming it.
 * - `MouseSensor` declines a right-click, and only a right-click. A middle-click starts a
 *   drag there, so it holds the card here.
 *
 * Only meaningful once the card's own `mousedown` handler has run: the prevented default
 * in the middle is that handler's.
 */
function isPressDndKitWillTake(event: MouseEvent<HTMLElement>): boolean {
  if (isSomebodyElsesEvent(event.target, event.currentTarget)) return false
  if (event.defaultPrevented) return false

  return event.button !== RIGHT_CLICK
}

/**
 * dnd-kit's activators, each declining an event the card does not own.
 *
 * `undefined` for a card that cannot be dragged, which is what dnd-kit hands back for a
 * disabled draggable, so a caller spreading the result spreads nothing.
 *
 * dnd-kit types the map by nothing more than the event name being a string, which is as
 * much as it knows about sensors it has not met, so this is where a handler becomes
 * something callable.
 */
function ownEventsOnly(listeners: DraggableSyntheticListeners): CardActivators | undefined {
  if (listeners === undefined) return undefined

  return Object.fromEntries(
    Object.entries(listeners).map(([name, activate]) => [
      name,
      (event: SyntheticEvent<HTMLElement>) => {
        if (isSomebodyElsesEvent(event.target, event.currentTarget)) return
        ;(activate as (event: SyntheticEvent<HTMLElement>) => void)(event)
      },
    ]),
  )
}

/**
 * Item, column and row keys are opaque, so any of them may hold whitespace, which an
 * HTML `id` may not.
 *
 * `_` is escaped along with everything else, rather than being carried through as a
 * word character. It is the escape marker, so leaving it literal would let a key
 * containing `_20_` produce the same output as a key containing a space. Escaping it
 * keeps the mapping injective, which is what stops two cards colliding on an id.
 */
function toElementId(raw: string): string {
  return raw.replace(/[^A-Za-z0-9:.-]/g, character => `_${character.charCodeAt(0).toString(16)}_`)
}
